import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, Button, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { PILE_CHASSE_FIXE } from '../theme/jetons';
import { jourCivilAujourdhui } from '../utils/dates';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { useLanguage } from '../contexts/LanguageContext';
import { numeroAffichable } from '../utils/numeroCommande';

const ReceiptFormDialog = ({ order, open, onClose, onSuccess, showSnackbar }) => {
  const { t } = useLanguage();
  const [receiptData, setReceiptData] = useState({ receipt_number: '', receipt_date: jourCivilAujourdhui(), comment: '', items: [] });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [survol, setSurvol] = useState(false);

  // Init des lignes depuis la commande à chaque ouverture
  useEffect(() => {
    if (open && order && order.items) {
      const items = order.items.map(item => {
        const remaining = item.remaining_quantity != null
          ? item.remaining_quantity
          : (item.quantity - item.received_quantity);
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity_ordered: item.quantity,
          quantity_received: item.received_quantity,
          remaining_quantity: remaining,
          unit_price: item.unit_price,
          quantity_to_receive: 0,
          comment: ''
        };
      });
      setReceiptData({ receipt_number: '', receipt_date: jourCivilAujourdhui(), comment: '', items });
      setAttachmentFile(null);
    }
  }, [open, order]);

  // ── CE QUE CETTE RÉCEPTION VA PRODUIRE, calculé une fois ────────────────
  // 🔴 ON MONTRE LE RÉSULTAT AVANT DE LE PRODUIRE — même famille que « Après
  // opération » du dialogue d'ajustement et « La commande sera soldée » du
  // paiement. C'est précisément ce qui manquait à l'ajustement silencieusement
  // annulé : un geste dont l'écran ne montrait pas la conséquence.
  const totalCommande = receiptData.items.reduce(
    (n, i) => n + (Number(i.quantity_ordered) || 0), 0);
  const dejaRecu = receiptData.items.reduce(
    (n, i) => n + (Number(i.quantity_received) || 0), 0);
  const jeRecois = receiptData.items.reduce(
    (n, i) => n + (Number(i.quantity_to_receive) || 0), 0);
  const apres = dejaRecu + jeRecois;
  const resteApres = totalCommande - apres;
  // Ce qu'il reste à prendre AVANT ce reçu — c'est lui qui décide si
  // « Tout recevoir » a encore un sens.
  const resteAPrendre = totalCommande - dejaRecu;

  const toutRecevoir = () => setReceiptData(prev => ({
    ...prev,
    items: prev.items.map(i => ({ ...i, quantity_to_receive: i.remaining_quantity })),
  }));

  const handleReceiptItemChange = (index, field, value) => {
    const updatedItems = [...receiptData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    if (field === 'quantity_to_receive') {
      const quantityToReceive = parseInt(value) || 0;
      const remaining = updatedItems[index].remaining_quantity;
      if (quantityToReceive > remaining) {
        showSnackbar(t('reception.quantityExceedsRemaining', { remaining }), 'warning');
        updatedItems[index].quantity_to_receive = remaining;
      }
    }
    setReceiptData(prev => ({ ...prev, items: updatedItems }));
  };

  const handleSubmitReceipt = async () => {
    if (!receiptData.receipt_number.trim()) {
      showSnackbar(t('reception.receiptNumberRequired'), 'warning');
      return;
    }
    const itemsToSubmit = receiptData.items
      .filter(item => item.quantity_to_receive > 0)
      .map(item => ({
        product_id: item.product_id,
        quantity_received: item.quantity_to_receive,
        unit_price: item.unit_price,
        comment: item.comment
      }));
    if (itemsToSubmit.length === 0) {
      showSnackbar(t('reception.quantityRequired'), 'warning');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify({
        receipt_number: receiptData.receipt_number,
        receipt_date: receiptData.receipt_date,
        comment: receiptData.comment,
        items: itemsToSubmit
      }));
      if (attachmentFile) formData.append('attachment', attachmentFile);

      await purchaseOrderService.createReceipt(order.id, formData, true);
      showSnackbar(t('reception.receiptCreated'), 'success');
      onClose && onClose();
      onSuccess && onSuccess();
    } catch (error) {
      showSnackbar(t('reception.receiptError', { error: error.message }), 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span">{t('reception.receiveDelivery')}</Typography>
        {order?.order_number && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ fontFamily: PILE_CHASSE_FIXE }}>
            {numeroAffichable(order.order_number)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {order && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* ⚠️ LE NUMÉRO DE REÇU EST ABSENT DE LA MAQUETTE MAIS IL RESTE :
                  mesuré, le serveur le REFUSE en 400 s'il manque
                  (`purchase_orders.py`, « Le numéro de reçu est requis »). Le
                  retirer produirait un formulaire qu'on ne peut pas valider.
                  *Une maquette peut omettre un champ que le serveur exige — c'est
                  au code de trancher, pas au dessin.* */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label={t('reception.receiptNumber')}
                  value={receiptData.receipt_number}
                  onChange={(e) => setReceiptData(prev => ({ ...prev, receipt_number: e.target.value }))}
                  placeholder={t('reception.receiptNumberPlaceholder')}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                {/* 🔴 LA DATE DE RÉCEPTION SE SAISIT ENFIN. Le modèle la range parmi
                    les quatre dates SAISIES du projet, mais la route ne la lisait
                    pas : elle retombait toujours sur « maintenant ». Réceptionner
                    une livraison arrivée la veille datait le reçu d'aujourd'hui. */}
                <TextField
                  fullWidth
                  type="date"
                  label={t('reception.receiptDate')}
                  value={receiptData.receipt_date}
                  onChange={(e) => setReceiptData(prev => ({ ...prev, receipt_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {/* ⚠️ LE DÉPÔT EST SUR LA MÊME LIGNE QUE LA DATE, celle-ci étroite à
                  gauche : le bon de livraison et la date de réception décrivent le
                  MÊME événement, et trois rangées pleine largeur les faisaient lire
                  comme trois formalités indépendantes. 5 + 3 + 4 = 12. */}
              <Grid item xs={12} md={4}>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="receipt-attachment-input"
                  type="file"
                  onChange={(e) => setAttachmentFile(e.target.files[0])}
                />
                {/* 🔴 UNE ZONE DE DÉPÔT, PAS UN BOUTON. Le bon de livraison arrive
                    en photo ou en PDF, souvent glissé depuis un dossier ou un
                    téléphone ; un bouton n'accepte que le clic. La zone accepte les
                    deux — le `label` reste, donc le clic marche toujours.
                    ⚠️ ET ELLE DIT QU'ELLE EST FACULTATIVE. Un cadre en pointillés
                    au milieu d'un formulaire se lit comme une obligation qu'on n'a
                    pas encore remplie. */}
                <label htmlFor="receipt-attachment-input" style={{ display: 'block' }}>
                  <Box
                    onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
                    onDragLeave={() => setSurvol(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setSurvol(false);
                      const fichier = e.dataTransfer?.files?.[0];
                      if (fichier) setAttachmentFile(fichier);
                    }}
                    sx={{
                      border: '1px dashed', borderRadius: 1, p: 2.5, cursor: 'pointer',
                      textAlign: 'center',
                      borderColor: survol ? 'primary.main' : 'divider',
                      bgcolor: survol ? 'action.hover' : 'transparent',
                    }}
                  >
                    <AttachFileIcon fontSize="small" color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      {attachmentFile ? attachmentFile.name : t('reception.deliveryNoteOptional')}
                    </Typography>
                  </Box>
                </label>
                {attachmentFile && (
                  <Button size="small" onClick={() => setAttachmentFile(null)} sx={{ mt: 1 }}>
                    {t('common.delete')}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('reception.comment')}
                  value={receiptData.comment}
                  onChange={(e) => setReceiptData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder={t('reception.optionalComment')}
                />
              </Grid>
            </Grid>

            {/* Le numéro est DÉJÀ dans le titre du dialogue : le répéter ici
                dirait deux fois la même chose sur le même écran. */}
            <Box sx={{ display: 'flex', alignItems: 'baseline',
                       justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h6">{t('reception.receivedItems')}</Typography>
              {/* 🔴 « TOUT RECEVOIR » REMPLIT CHAQUE LIGNE AVEC SON RESTANT, jamais
                  avec la quantité commandée : le serveur refuse en 400 au-delà
                  (`purchase_orders.py:706`), donc le restant est EXACTEMENT le
                  maximum acceptable.
                  ⚠️ Une ligne déjà complète reste à 0 — *une ligne complète n'est
                  pas une exception à traiter, c'est une ligne qui n'a rien à
                  recevoir.* Et le lien DISPARAÎT quand il n'y a plus rien à
                  recevoir, au lieu d'être grisé : même doctrine que « Combler ». */}
              {resteAPrendre > 0 && (
                <Button size="small" onClick={toutRecevoir}>
                  {t('reception.receiveAll')}
                </Button>
              )}
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {/* 🔴 TROIS COLONNES RETIRÉES, ET CHACUNE POUR SA RAISON.
                        · « Restant » est une SOUSTRACTION entre ses deux voisines,
                          et c'est déjà le `max` du champ de saisie ;
                        · « Prix unit. » ne se saisit pas ici et ne change pas :
                          c'est une donnée de commande, pas de réception ;
                        · « Commentaire » par ligne est ÉCRIT, SERVI, ET JAMAIS RELU
                          — mesuré : les lignes de reçu s'affichent en quatre
                          colonnes dans le détail (Produit, Qté reçue, PU, Total),
                          sans commentaire, et les 3 lignes de la base sont vides.
                          *Un champ qu'on peut remplir et qu'on ne revoit jamais
                          promet une trace qu'il ne garde pas.* La colonne reste en
                          base ; c'est la SAISIE qui part. Le commentaire du REÇU,
                          lui, est saisi ET affiché : il reste. */}
                    <TableCell>{t('products.name')}</TableCell>
                    <TableCell align="right">{t('reception.ordered')}</TableCell>
                    <TableCell align="right">{t('reception.alreadyReceived')}</TableCell>
                    <TableCell align="right">{t('reception.toReceive')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receiptData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="right">{item.quantity_ordered}</TableCell>
                      <TableCell align="right">{item.quantity_received}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity_to_receive}
                          onChange={(e) => handleReceiptItemChange(index, 'quantity_to_receive', e.target.value)}
                          disabled={item.remaining_quantity === 0}
                          inputProps={{ min: 0, max: item.remaining_quantity }}
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>


          </Box>
        )}
      </DialogContent>
      {/* ═══ LA PHRASE DE CONSÉQUENCE, SUR LA LIGNE DES BOUTONS ═══════════════
          🔴 DU TEXTE AMBRE, PAS UN APLAT PLEINE LARGEUR. C'est le même correctif
          que les cartes de la Comptabilité : *sur une bande pleine largeur, la
          couleur pèse plus que la phrase qu'elle porte* — et une bande ambrée au
          bas d'un formulaire se lit comme une alerte, alors que c'est une
          prévision.
          ⚠️ ET ELLE EST SUR LA MÊME LIGNE QUE « VALIDER », parce que c'est là
          qu'on la lit : au moment de décider. Une conséquence annoncée loin du
          geste qu'elle décrit est une conséquence qu'on ne lit pas.
          La teinte reste porteuse de sens — ambre s'il reste, vert si la commande
          sera complète : deux ISSUES, pas deux degrés. */}
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2, gap: 2 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          color={resteApres > 0 ? 'warning.main' : 'success.main'}
          sx={{ visibility: jeRecois > 0 ? 'visible' : 'hidden' }}
        >
          {t('reception.afterThisReceipt', { recu: apres, total: totalCommande })}
          {' — '}
          {resteApres > 0
            ? t('reception.willRemain', { count: resteApres })
            : t('reception.willBeComplete')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        {/* 🔴 « VALIDER LA RÉCEPTION », PAS « CRÉER LE REÇU ». On nomme le GESTE
            MÉTIER, pas l'objet que le logiciel crée au passage : celui qui clique
            réceptionne une livraison, il ne crée pas un enregistrement. Même
            correction que « Encaisser » à la place de « + » en Comptabilité.
            ⚠️ Et l'icône « + » s'en va avec : elle disait « ajouter un objet ». */}
        <Button onClick={handleSubmitReceipt} variant="contained" color="primary">
          {t('reception.validateReception')}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptFormDialog;
